import { useState, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { colaboradoresService, type CreateColaboradorDto, type UpdateColaboradorDto, type Colaborador } from '../../services/colaboradores.service';
import { cargosService } from '../../services/cargos.service';
import {
  agendamentosService,
  type HistoricoAgendamentoItem,
  type ValidarPlanilhaResult,
} from '../../services/agendamentos.service';
import { DomainService } from '../../services/domain.service';
import { useStepNavigation } from '../../hooks/useStepNavigation';
import StepIndicator from '../../components/StepIndicator';
import EmployeeForm from '../../components/Forms/EmployeeForm';
import CollaboratorSearch from '../../components/Forms/CollaboratorSearch';
import ColaboradorClinicaPanel from '../../components/Forms/ColaboradorClinicaPanel';
import { formatCPF, maskEmail, maskTelefone } from '../../utils/formatters';
import HistoricoAgendamentos from './HistoricoAgendamentos';
import UploadLotePlanilha from './UploadLotePlanilha';
import EditarContatoModal from './components/EditarContatoModal';
import { limparRascunhoAgendamento } from '../../utils/agendamentoStorage';
import { getHojeISO, getDeslocamentoDiasISO } from '../../utils/dateUtils';
import { useEmpresa } from '../../contexts/EmpresaContext';
import { clinicasService } from '../../services/clinicas.service';
import api from '../../services/api';
import ExamesPontuaisSearchInput, { type ExameItemCatalog } from './components/ExamesPontuaisSearchInput';
import {
  Calendar,
  User,
  Users,
  Search,
  FileSpreadsheet,
  ArrowLeft,
  ChevronRight,
  ClipboardList,
  Edit3,
  Trash2,
  Plus,
  Sparkles,
  AlertTriangle,
  UserCheck,
  UserPlus,
  Building,
  Clock,
  Mail,
  Phone,
} from 'lucide-react';

interface Employee {
  id: string;
  nome: string;
  nome_social?: string;
  pcd?: boolean;
  enviar_esocial?: boolean;
  regime_contratacao?: string;
  estado_civil?: string;
  cpf: string;
  nascimento?: string;
  sexo?: string;
  telefone?: string;
  email?: string;
  cargo?: string;
  setor?: string;
  formacao?: string;
  dataAdmissao?: string;
  demissao?: string;
  exame?: string;
  variacao?: string;
  clinica?: string;
  clinicaNome?: string;
  dataAgendamento?: string;
  examesSelecionados?: number[];
  examesSelecionadosCount?: number;
  protocoloExames?: number[];
  examesPontuais?: number[];
  modeloExame?: string;
  tipoContrato?: string;
  is_candidato?: boolean;
  escolha_clinica_tipo?: 'rh' | 'colaborador';
  cargo_id?: number;
  setor_id?: number;
  matricula_esocial?: string;
  is_existente?: boolean;
}

type ViewMode =
  | 'hub'
  | 'historico'
  | 'modo_selecao'
  | 'lote_escolha'
  | 'individual'
  | 'lote_manual'
  | 'lote_planilha';

const formatDateForAPI = (dateStr?: string | null): string | undefined => {
  if (!dateStr || typeof dateStr !== 'string') return undefined;
  const clean = dateStr.trim();
  if (!clean) return undefined;
  if (clean.match(/^\d{4}-\d{2}-\d{2}$/)) return clean;
  if (clean.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
    const [dia, mes, ano] = clean.split('/');
    return `${ano}-${mes}-${dia}`;
  }
  if (clean.includes('T')) {
    return clean.split('T')[0];
  }
  return undefined;
};

export default function CadastroPage() {
  const { empresa } = useEmpresa();
  const { currentStep, stepData, saveStepData, goToNextStep, resetAllData } = useStepNavigation();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');

  // Inicializa o modo de visão baseado na query string ou no rascunho existente
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    if (tabParam === 'historico' || tabParam === 'autoagendamentos') return 'historico';
    if (tabParam === 'lote_manual') return 'lote_manual';
    if (tabParam === 'individual') return 'individual';

    // Se já existem colaboradores no rascunho, manter o usuário diretamente na fila
    try {
      const saved = localStorage.getItem('customer_employeesData');
      const savedEmpresaId = localStorage.getItem('customer_employeesData_empresa_id');
      const currentEmpresaId = localStorage.getItem('customer_empresa_id');

      if (saved && (!savedEmpresaId || (currentEmpresaId && savedEmpresaId !== currentEmpresaId))) {
        limparRascunhoAgendamento();
      } else if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0) {
          const hasValid = parsed.some(
            (p: Employee) => p && typeof p === 'object' && (Boolean(p.nome?.trim()) || Boolean(p.cpf?.trim()))
          );
          if (hasValid) return 'lote_manual';
        }
      }
    } catch (e) {
      void e;
    }

    if (tabParam === 'novo') return 'modo_selecao';
    return 'hub';
  });

  // Inicializar colaboradores diretamente do localStorage para persistência atômica entre reloads/abas
  const [employees, setEmployees] = useState<Employee[]>(() => {
    try {
      const saved = localStorage.getItem('customer_employeesData');
      const savedEmpresaId = localStorage.getItem('customer_employeesData_empresa_id');
      const currentEmpresaId = localStorage.getItem('customer_empresa_id');

      if (saved && (!savedEmpresaId || (currentEmpresaId && savedEmpresaId !== currentEmpresaId))) {
        limparRascunhoAgendamento();
        return [];
      }

      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) {
          // Filtrar estritamente colaboradores válidos com nome ou cpf preenchido
          const valid = parsed.filter(
            (p: Employee) => p && typeof p === 'object' && (Boolean(p.nome?.trim()) || Boolean(p.cpf?.trim()))
          );
          if (valid.length > 0) {
            return valid;
          } else {
            limparRascunhoAgendamento();
          }
        }
      }
    } catch (e) {
      console.warn('⚠️ Erro ao restaurar rascunho de colaboradores do localStorage:', e);
      limparRascunhoAgendamento();
    }
    return [];
  });
  const [editingEmployee, setEditingEmployee] = useState<Employee | null>(null);
  const [editingContatoEmployee, setEditingContatoEmployee] = useState<Employee | null>(null);
  const [loading, setLoading] = useState(false);
  const [formKey, setFormKey] = useState(0);
  const [isNovoCadastroOpen, setIsNovoCadastroOpen] = useState(false);
  const [modoInclusao, setModoInclusao] = useState<'novo_candidato' | 'em_contratacao' | 'ativo_base'>('novo_candidato');
  const [cardAnimState, setCardAnimState] = useState<'entering' | 'exiting'>('entering');

  // Helper para persistir rascunho de colaboradores sincronizado com o ID da empresa ativa
  const syncEmployeesStorage = (items: Employee[]) => {
    localStorage.setItem('customer_employeesData', JSON.stringify(items));
    const currentEmpresaId = empresa?.id ? empresa.id.toString() : localStorage.getItem('customer_empresa_id');
    if (currentEmpresaId) {
      localStorage.setItem('customer_employeesData_empresa_id', currentEmpresaId);
    }
  };

  const prevEmpresaIdRef = useRef<number | null>(empresa?.id ?? null);

  // Monitorar se a empresa ativa mudar enquanto estiver na tela de cadastro
  useEffect(() => {
    if (!empresa?.id) return;
    const currentEmpresaId = empresa.id.toString();
    const savedEmpresaId = localStorage.getItem('customer_employeesData_empresa_id');

    const empresaMudou = prevEmpresaIdRef.current !== null && prevEmpresaIdRef.current !== empresa.id;
    const rascunhoDivergente = Boolean(savedEmpresaId && savedEmpresaId !== currentEmpresaId);

    if (empresaMudou || rascunhoDivergente) {
      console.log('🔄 [Cadastro] Empresa alterada ou rascunho divergente. Limpando rascunho e redirecionando para início.');
      limparRascunhoAgendamento();
      setEmployees([]);
      window.location.href = '/';
      return;
    }

    prevEmpresaIdRef.current = empresa.id;
  }, [empresa?.id]);

  // Tipos de exame para configuração de exames
  const [tiposExame, setTiposExame] = useState<Array<{ id: number; nome: string }>>([]);
  const [catalogExames, setCatalogExames] = useState<ExameItemCatalog[]>([]);

  useEffect(() => {
    Promise.all([
      api.get<ExameItemCatalog[]>('/exames-clinicos').then((r) => r.data).catch(() => []),
      clinicasService.getExamesClinicosClinica(1).catch(() => []),
    ])
      .then(([allExames, clinicaExames]) => {
        const map = new Map<number, ExameItemCatalog>();
        if (Array.isArray(allExames)) {
          allExames.forEach((e) => {
            if (e?.id && e?.nome) map.set(e.id, e);
          });
        }
        if (Array.isArray(clinicaExames)) {
          clinicaExames.forEach((e: { id?: number; nome?: string; valor?: number; sigla?: string }) => {
            if (e?.id && e?.nome && !map.has(e.id)) {
              map.set(e.id, e as ExameItemCatalog);
            }
          });
        }
        setCatalogExames(Array.from(map.values()));
      })
      .catch((err) => {
        console.warn('Erro ao carregar catálogo de exames clínicos:', err);
      });
  }, []);

  useEffect(() => {
    DomainService.getExamTypes()
      .then((res) => {
        if (Array.isArray(res) && res.length > 0) {
          setTiposExame(res);
        }
      })
      .catch((err) => {
        console.warn('Erro ao carregar tipos de exame:', err);
        setTiposExame([
          { id: 1, nome: 'Admissional' },
          { id: 2, nome: 'Demissional' },
          { id: 3, nome: 'Periódico' },
          { id: 4, nome: 'Retorno ao Trabalho' },
          { id: 5, nome: 'Mudança de Riscos Ocupacionais' },
        ]);
      });
  }, []);

  const periodicoId = useMemo(() => {
    const p = tiposExame.find((t) => t.nome.toLowerCase().includes('periódico') || t.nome.toLowerCase().includes('periodico'));
    return p ? p.id.toString() : '3';
  }, [tiposExame]);

  const demissionalId = useMemo(() => {
    const d = tiposExame.find((t) => t.nome.toLowerCase().includes('demissional'));
    return d ? d.id.toString() : '2';
  }, [tiposExame]);

  const admissionalId = useMemo(() => {
    const a = tiposExame.find((t) => t.nome.toLowerCase().includes('admissional'));
    return a ? a.id.toString() : '1';
  }, [tiposExame]);

  // O formulário de cadastro de novo colaborador/candidato abre quando solicitado ou editando
  const shouldShowForm = editingEmployee !== null || isNovoCadastroOpen || (viewMode === 'individual' && employees.length === 0);

  // Navegação animada entre telas
  const navigateToMode = (target: ViewMode, callback?: () => void) => {
    setCardAnimState('exiting');
    setTimeout(() => {
      if (callback) callback();
      setViewMode(target);
      setCardAnimState('entering');
    }, 220);
  };

  // Estatísticas de agendamentos existentes
  const [historicoStats, setHistoricoStats] = useState<{
    total: number;
    autoagendamentos: number;
    rh: number;
    candidatos: number;
    concluidos: number;
  }>({
    total: 0,
    autoagendamentos: 0,
    rh: 0,
    candidatos: 0,
    concluidos: 0,
  });

  // Carregar histórico para alimentar badges e contadores da entrada em sintonia com a tela de histórico
  useEffect(() => {
    async function carregarStats() {
      try {
        const ags: HistoricoAgendamentoItem[] = await agendamentosService.getHistorico();
        if (ags && Array.isArray(ags)) {
          const hojeStr = getHojeISO();
          const limite30Str = getDeslocamentoDiasISO(30);

          const isFuturoOuPendente = (item: HistoricoAgendamentoItem) => {
            if (item.status_badge === 'red' || item.status === 'Cancelado' || item.status === 'Não Compareceu') return false;
            if (item.status === 'Realizado' || item.status === 'Concluído' || item.status_badge === 'green') return false;
            if (!item.data_agendamento) return true;
            const itemDateStr = item.data_agendamento.includes('T')
              ? item.data_agendamento.split('T')[0]
              : item.data_agendamento.substring(0, 10);
            return itemDateStr >= hojeStr;
          };

          const matchesDefaultPeriodo = (item: HistoricoAgendamentoItem) => {
            if (item.data_agendamento) {
              const itemDate = item.data_agendamento.includes('T')
                ? item.data_agendamento.split('T')[0]
                : item.data_agendamento.substring(0, 10);
              return itemDate >= hojeStr && itemDate <= limite30Str;
            }
            return item.status_badge !== 'red' && item.status !== 'Cancelado' && item.status !== 'Não Compareceu';
          };

          // Agendamentos ativos exibidos por default no painel (futuros + próximos 30 dias)
          const agsDefault = ags.filter((a) => matchesDefaultPeriodo(a) && isFuturoOuPendente(a));

          // Concluídos dentro do período padrão (últimos 30 dias até hoje)
          const concluidosPeriodo = ags.filter((a) => {
            const ehConcluido = a.status_badge === 'green' || a.status === 'Realizado' || a.status === 'Concluído';
            if (!ehConcluido) return false;
            if (a.data_agendamento) {
              const itemDate = a.data_agendamento.includes('T')
                ? a.data_agendamento.split('T')[0]
                : a.data_agendamento.substring(0, 10);
              return itemDate >= getDeslocamentoDiasISO(-30) && itemDate <= limite30Str;
            }
            return true;
          });

          setHistoricoStats({
            total: agsDefault.length,
            autoagendamentos: agsDefault.filter((a) => a.escolha_clinica_tipo === 'colaborador').length,
            rh: agsDefault.filter((a) => a.escolha_clinica_tipo !== 'colaborador').length,
            candidatos: agsDefault.filter((a) => a.is_candidato).length,
            concluidos: concluidosPeriodo.length,
          });
        }
      } catch (err: unknown) {
        console.warn('Não foi possível carregar estatísticas do histórico:', err);
      }
    }
    carregarStats();
  }, []);

  // Sincronizar dados de colaboradores existentes no stepData se houver
  useEffect(() => {
    if (stepData.employeesData && stepData.employeesData.length > 0 && employees.length === 0) {
      const valid = (stepData.employeesData as Employee[]).filter(
        (p) => p && typeof p === 'object' && (Boolean(p.nome?.trim()) || Boolean(p.cpf?.trim()))
      );
      if (valid.length > 0) {
        setEmployees(valid);
      }
    }
  }, [stepData.employeesData, employees.length]);

  // Sincronizar parâmetro de URL com viewMode
  useEffect(() => {
    if (tabParam === 'historico' || tabParam === 'autoagendamentos') {
      setViewMode('historico');
    } else if (tabParam === 'lote_manual') {
      setViewMode('lote_manual');
    } else if (tabParam === 'individual') {
      setViewMode('individual');
    } else if (tabParam === 'novo') {
      if (employees.length > 0) {
        setViewMode('lote_manual');
      } else {
        setViewMode('modo_selecao');
      }
    } else if (!tabParam && employees.length > 0) {
      setViewMode('lote_manual');
    }
  }, [tabParam, employees.length]);

  const handleSelectTab = (tab: 'historico' | 'novo') => {
    if (tab === 'historico') {
      navigateToMode('historico', () => setSearchParams({ tab: 'historico' }));
    } else {
      navigateToMode('modo_selecao', () => setSearchParams({ tab: 'novo' }));
    }
  };

  const handleDescartarRascunho = () => {
    if (confirm('Deseja realmente descartar os colaboradores deste rascunho?')) {
      limparRascunhoAgendamento();
      setEmployees([]);
      setEditingEmployee(null);
      resetAllData();
      setViewMode('hub');
    }
  };

  const handleContinuarRascunho = () => {
    const valid = employees.filter(
      (p) => p && typeof p === 'object' && (Boolean(p.nome?.trim()) || Boolean(p.cpf?.trim()))
    );

    if (valid.length > 0) {
      navigateToMode('lote_manual', () => setSearchParams({ tab: 'lote_manual' }));
    } else {
      // Se não há dados válidos, limpa e vai direto para a tela de escolha
      setEmployees([]);
      limparRascunhoAgendamento();
      saveStepData({ employeesData: [] });
      navigateToMode('modo_selecao', () => setSearchParams({ tab: 'novo' }));
    }
  };

  const handleAddEmployee = async (employee: Employee) => {
    try {
      setLoading(true);

      if (editingEmployee) {
        if (!editingEmployee.id.startsWith('local_') && !editingEmployee.id.startsWith('batch_')) {
          const colaboradorId = parseInt(editingEmployee.id);
          const updateData: UpdateColaboradorDto = {
            nome: employee.nome?.trim(),
            cpf: employee.cpf ? employee.cpf.replace(/\D/g, '') : undefined,
            data_nascimento: employee.nascimento ? formatDateForAPI(employee.nascimento) : undefined,
            sexo: employee.sexo ? ((employee.sexo as string).toUpperCase() as 'M' | 'F') : undefined,
            telefone: employee.telefone && employee.telefone.trim() !== '' ? employee.telefone.trim() : undefined,
            email: employee.email && employee.email.trim() !== '' ? employee.email.trim() : undefined,
            is_candidato: employee.is_candidato ?? false,
          };

          if (employee.formacao) updateData.formacao_academica_id = parseInt(employee.formacao);
          if (employee.dataAdmissao) updateData.data_admissao = formatDateForAPI(employee.dataAdmissao);
          if (employee.demissao) updateData.data_demissao = formatDateForAPI(employee.demissao);
          if (employee.matricula_esocial) updateData.matricula_esocial = employee.matricula_esocial.trim();
          if (employee.cargo) updateData.cargo_nome = employee.cargo.trim();
          if (employee.setor) updateData.setor_nome = employee.setor.trim();
          if (employee.nome_social) updateData.nome_social = employee.nome_social.trim();
          if (employee.pcd !== undefined) updateData.pcd = employee.pcd;
          if (employee.enviar_esocial !== undefined) updateData.enviar_esocial = employee.enviar_esocial;
          if (employee.regime_contratacao) updateData.regime_contratacao = employee.regime_contratacao;
          if (employee.estado_civil) updateData.estado_civil = employee.estado_civil;

          try {
            await colaboradoresService.update(colaboradorId, updateData);
          } catch (errApi) {
            console.warn('⚠️ Não foi possível atualizar colaborador na API (salvo localmente):', errApi);
          }
        }

        const updatedEmployees = employees.map((emp) =>
          emp.id === editingEmployee.id
            ? { ...employee, is_existente: editingEmployee.is_existente }
            : emp
        );
        setEmployees(updatedEmployees);
        saveStepData({ employeesData: updatedEmployees });
        syncEmployeesStorage(updatedEmployees);
        setEditingEmployee(null);
        setIsNovoCadastroOpen(false);
        setFormKey((prev) => prev + 1);

        if (viewMode === 'individual') {
          goToNextStep();
          return;
        }

        setTimeout(() => {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }, 50);
      } else {
        const isCandidate = Boolean(
          employee.is_candidato ||
          modoInclusao === 'novo_candidato' ||
          modoInclusao === 'em_contratacao' ||
          !employee.dataAdmissao
        );
        const cleanCpf = (employee.cpf || '').replace(/\D/g, '');
        const colIdNum = parseInt(String(employee.id));
        const isAlreadyPersisted =
          !isNaN(colIdNum) &&
          !String(employee.id).startsWith('local_') &&
          !String(employee.id).startsWith('batch_');

        employee.is_candidato = isCandidate;
        if (!employee.exame || employee.exame === '') {
          employee.exame = isCandidate ? (admissionalId || '1') : (admissionalId || '1');
        }

        if (!isAlreadyPersisted) {
          employee.is_existente = false;
          if (isCandidate && !employee.escolha_clinica_tipo) {
            employee.escolha_clinica_tipo = 'colaborador';
          }

          try {
            const createData: CreateColaboradorDto = {
              empresa_id: empresa?.id,
              nome: employee.nome.trim(),
              cpf: cleanCpf,
              data_nascimento: employee.nascimento ? formatDateForAPI(employee.nascimento) : '2000-01-01',
              sexo: (employee.sexo || 'M').toUpperCase() as 'M' | 'F',
              telefone: employee.telefone && employee.telefone.trim() !== '' ? employee.telefone.trim() : undefined,
              email: employee.email && employee.email.trim() !== '' ? employee.email.trim() : undefined,
              is_candidato: isCandidate,
            };

            if (employee.formacao) createData.formacao_academica_id = parseInt(employee.formacao);
            if (employee.dataAdmissao) createData.data_admissao = formatDateForAPI(employee.dataAdmissao);
            if (employee.demissao) createData.data_demissao = formatDateForAPI(employee.demissao);
            if (employee.matricula_esocial) createData.matricula_esocial = employee.matricula_esocial.trim();
            if (employee.cargo) createData.cargo_nome = employee.cargo.trim();
            if (employee.setor) createData.setor_nome = employee.setor.trim();
            if (employee.nome_social) createData.nome_social = employee.nome_social.trim();
            if (employee.pcd !== undefined) createData.pcd = employee.pcd;
            if (employee.enviar_esocial !== undefined) createData.enviar_esocial = employee.enviar_esocial;
            if (employee.regime_contratacao) createData.regime_contratacao = employee.regime_contratacao;
            if (employee.estado_civil) createData.estado_civil = employee.estado_civil;

            const colaboradorCriado = await colaboradoresService.create(createData);
            if (colaboradorCriado?.id) {
              employee.id = colaboradorCriado.id.toString();
            }
          } catch (errApi: unknown) {
            console.warn('⚠️ Tentativa de criação de colaborador retornou aviso/erro. Verificando se já existe na base:', errApi);
            // Se o colaborador já existe nesta empresa (ex: cadastrado previamente), reaproveitar o ID existente
            try {
              const resExisting = await colaboradoresService.findAll({
                search: cleanCpf,
                empresa_id: empresa?.id,
                enabled: 'all',
                limit: 5,
              });
              const match = (resExisting?.data || []).find(
                (c) => (c.cpf || '').replace(/\D/g, '') === cleanCpf,
              );
              if (match?.id) {
                employee.id = match.id.toString();
                employee.is_existente = true;
                console.log(`✅ [Cadastro] Colaborador ${match.nome} (CPF ${cleanCpf}) vinculado ao cadastro existente #${match.id}.`);
              } else {
                if (!employee.id || !employee.id.startsWith('local_')) {
                  employee.id = `local_${Date.now()}`;
                }
              }
            } catch {
              if (!employee.id || !employee.id.startsWith('local_')) {
                employee.id = `local_${Date.now()}`;
              }
            }
          }
        } else {
          employee.is_existente = !isCandidate;
          if (isCandidate && !employee.escolha_clinica_tipo) {
            employee.escolha_clinica_tipo = 'colaborador';
          }
        }

        if (employee.cargo_id && (!employee.examesSelecionados || employee.examesSelecionados.length === 0)) {
          try {
            const resCargo = await cargosService.getExamesByCargo(
              employee.cargo_id,
              empresa?.id,
              employee.setor_id,
              employee.exame || admissionalId,
            );
            if (resCargo && Array.isArray(resCargo.exames_clinicos_ids)) {
              employee.examesSelecionados = resCargo.exames_clinicos_ids;
              employee.protocoloExames = resCargo.exames_clinicos_ids;
              employee.examesPontuais = [];
              employee.examesSelecionadosCount = resCargo.exames_clinicos_ids.length;
            }
          } catch {
            // fallback
          }
        }

        if (viewMode === 'individual') {
          const newEmployees = [employee];
          setEmployees(newEmployees);
          saveStepData({ employeesData: newEmployees });
          syncEmployeesStorage(newEmployees);
          goToNextStep();
          return;
        } else {
          const newEmployees = [...employees, employee];
          setEmployees(newEmployees);
          saveStepData({ employeesData: newEmployees });
          syncEmployeesStorage(newEmployees);
          setIsNovoCadastroOpen(false);
          setFormKey((prev) => prev + 1);

          setTimeout(() => {
            const filaEl = document.getElementById('fila-agendamento-section');
            if (filaEl) {
              filaEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
            } else {
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }
          }, 50);
        }
      }
    } catch (error: unknown) {
      console.error('Erro ao adicionar colaborador:', error);
      alert('Ocorreu um erro ao registrar o colaborador.');
    } finally {
      setLoading(false);
    }
  };

  // Inclusão rápida de colaborador já existente na base (multi-inclusão sem fechar o dropdown)
  const handleIncluirColaboradorExistente = async (colaborador: Colaborador) => {
    const colabIdStr = String(colaborador.id);
    const cleanCpf = (colaborador.cpf || '').replace(/\D/g, '');

    const jaIncluso = employees.some(
      (e) => e.id === colabIdStr || (cleanCpf && (e.cpf || '').replace(/\D/g, '') === cleanCpf)
    );

    if (jaIncluso) return;

    let examesComplementaresIds: number[] = [];
    let variacaoId = '1';

    const isColabCandidato = !colaborador.data_admissao || Boolean((colaborador as unknown as { is_candidato?: boolean }).is_candidato);
    const initialExame = isColabCandidato ? (admissionalId || '1') : (periodicoId || '3');

    const cargoId = colaborador.cargo?.id || (colaborador as unknown as { cargo_id?: number }).cargo_id;
    const setorId = colaborador.setor?.id || (colaborador as unknown as { setor_id?: number }).setor_id;
    if (cargoId) {
      try {
        const resCargo = await cargosService.getExamesByCargo(
          cargoId,
          empresa?.id,
          setorId ? Number(setorId) : undefined,
          initialExame,
        );
        if (resCargo && Array.isArray(resCargo.exames_clinicos_ids) && resCargo.exames_clinicos_ids.length > 0) {
          examesComplementaresIds = resCargo.exames_clinicos_ids;
        }
        if (resCargo?.variacoes?.[0]?.id) {
          variacaoId = String(resCargo.variacoes[0].id);
        }
      } catch (errCargo) {
        console.warn('⚠️ Não foi possível carregar exames complementares do cargo:', errCargo);
      }
    }

    const novoItem: Employee = {
      id: colabIdStr,
      nome: colaborador.nome,
      nome_social: colaborador.nome_social || undefined,
      pcd: colaborador.pcd ?? undefined,
      enviar_esocial: colaborador.enviar_esocial ?? undefined,
      regime_contratacao: colaborador.regime_contratacao || undefined,
      estado_civil: colaborador.estado_civil || undefined,
      cpf: formatCPF(colaborador.cpf),
      nascimento: colaborador.data_nascimento ? colaborador.data_nascimento.split('T')[0] : undefined,
      sexo: colaborador.sexo || undefined,
      telefone: colaborador.telefone || undefined,
      email: colaborador.email || undefined,
      cargo: colaborador.cargo?.nome || '',
      cargo_id: cargoId ? Number(cargoId) : undefined,
      setor: colaborador.setor?.nome || '',
      setor_id: setorId ? Number(setorId) : undefined,
      formacao: colaborador.formacao_academica_id ? String(colaborador.formacao_academica_id) : undefined,
      dataAdmissao: colaborador.data_admissao ? colaborador.data_admissao.split('T')[0] : undefined,
      demissao: colaborador.data_demissao ? colaborador.data_demissao.split('T')[0] : undefined,
      matricula_esocial: colaborador.matricula_esocial || undefined,
      tipoContrato: colaborador.tipo_id ? String(colaborador.tipo_id) : undefined,
      is_candidato: isColabCandidato,
      is_existente: !isColabCandidato,
      exame: initialExame,
      escolha_clinica_tipo: isColabCandidato ? 'colaborador' : 'rh',
      variacao: variacaoId,
      examesSelecionados: examesComplementaresIds,
      protocoloExames: examesComplementaresIds,
      examesPontuais: [],
      examesSelecionadosCount: examesComplementaresIds.length,
      modeloExame: '0',
    };

    const newEmployees = [...employees, novoItem];
    setEmployees(newEmployees);
    saveStepData({ employeesData: newEmployees });
    syncEmployeesStorage(newEmployees);

    if (viewMode === 'individual' && newEmployees.length > 1) {
      setViewMode('lote_manual');
      setSearchParams({ tab: 'lote_manual' });
    }
  };

  const handleUpdateEmployeeExam = async (employeeId: string, examId: string) => {
    let complementaresUpdated: number[] | null = null;
    const targetEmp = employees.find((e) => e.id === employeeId);
    if (targetEmp?.cargo_id) {
      try {
        const resCargo = await cargosService.getExamesByCargo(
          targetEmp.cargo_id,
          empresa?.id,
          targetEmp.setor_id,
          examId,
        );
        complementaresUpdated = resCargo.exames_clinicos_ids || [];
      } catch (err) {
        console.warn('⚠️ Erro ao atualizar exames do cargo ao alterar tipo de exame:', err);
      }
    }

    const updated = employees.map((emp) => {
      if (emp.id === employeeId) {
        const examesProtocolo = complementaresUpdated !== null ? complementaresUpdated : (emp.protocoloExames || []);
        const pontuais = emp.examesPontuais || [];
        const examesSelecionados = Array.from(new Set([...examesProtocolo, ...pontuais]));
        return {
          ...emp,
          exame: examId,
          demissao: examId === demissionalId ? emp.demissao : undefined,
          protocoloExames: examesProtocolo,
          examesPontuais: pontuais,
          examesSelecionados,
          examesSelecionadosCount: examesSelecionados.length,
        };
      }
      return emp;
    });
    setEmployees(updated);
    saveStepData({ employeesData: updated });
    syncEmployeesStorage(updated);
  };

  const handleAddPontualExam = (employeeId: string, examId: number) => {
    setEmployees((prev) => {
      const updated = prev.map((emp) => {
        if (emp.id !== employeeId) return emp;
        const currentSelected = emp.examesSelecionados || [];
        if (currentSelected.includes(examId)) return emp;

        const newSelected = [...currentSelected, examId];
        const newPontuais = [...(emp.examesPontuais || []), examId];
        return {
          ...emp,
          examesSelecionados: newSelected,
          examesPontuais: newPontuais,
          examesSelecionadosCount: newSelected.length,
        };
      });
      saveStepData({ employeesData: updated });
      syncEmployeesStorage(updated);
      return updated;
    });
  };

  const handleRemovePontualExam = (employeeId: string, examId: number) => {
    setEmployees((prev) => {
      const updated = prev.map((emp) => {
        if (emp.id !== employeeId) return emp;
        const newSelected = (emp.examesSelecionados || []).filter((id) => id !== examId);
        const newPontuais = (emp.examesPontuais || []).filter((id) => id !== examId);
        return {
          ...emp,
          examesSelecionados: newSelected,
          examesPontuais: newPontuais,
          examesSelecionadosCount: newSelected.length,
        };
      });
      saveStepData({ employeesData: updated });
      syncEmployeesStorage(updated);
      return updated;
    });
  };

  const handleUpdateEmployeeDemissao = (employeeId: string, dataDemissao: string) => {
    const updated = employees.map((emp) => {
      if (emp.id === employeeId) {
        return { ...emp, demissao: dataDemissao };
      }
      return emp;
    });
    setEmployees(updated);
    saveStepData({ employeesData: updated });
    syncEmployeesStorage(updated);
  };

  const handleUpdateEmployeeClinicaTipo = (employeeId: string, tipo: 'rh' | 'colaborador') => {
    const updated = employees.map((emp) => {
      if (emp.id === employeeId) {
        if (tipo === 'colaborador') {
          return {
            ...emp,
            escolha_clinica_tipo: tipo,
            clinica: undefined,
            clinicaNome: undefined,
            dataAgendamento: undefined,
          };
        }
        return { ...emp, escolha_clinica_tipo: tipo };
      }
      return emp;
    });
    setEmployees(updated);
    saveStepData({ employeesData: updated });
    syncEmployeesStorage(updated);
  };

  const handleUpdateEmployeeClinica = (employeeId: string, clinicaId: string, clinicaNome: string) => {
    const updated = employees.map((emp) => {
      if (emp.id === employeeId) {
        return {
          ...emp,
          clinica: clinicaId,
          clinicaNome: clinicaNome,
          dataAgendamento: '',
        };
      }
      return emp;
    });
    setEmployees(updated);
    saveStepData({ employeesData: updated });
    syncEmployeesStorage(updated);
  };

  const handleUpdateEmployeeDataAgendamento = (employeeId: string, dataAgendamento: string) => {
    const updated = employees.map((emp) => {
      if (emp.id === employeeId) {
        return { ...emp, dataAgendamento };
      }
      return emp;
    });
    setEmployees(updated);
    saveStepData({ employeesData: updated });
    syncEmployeesStorage(updated);
  };

  const handleUpdateEmployeeContato = (
    employeeId: string,
    contato: { email?: string; telefone?: string },
  ) => {
    const updated = employees.map((emp) => {
      if (emp.id === employeeId) {
        return {
          ...emp,
          email: contato.email !== undefined ? contato.email : emp.email,
          telefone: contato.telefone !== undefined ? contato.telefone : emp.telefone,
        };
      }
      return emp;
    });
    setEmployees(updated);
    saveStepData({ employeesData: updated });
    syncEmployeesStorage(updated);
  };

  const handleEditEmployee = (index: number) => {
    setEditingEmployee(employees[index]);
    setIsNovoCadastroOpen(true);
    setTimeout(() => {
      const formEl = document.getElementById('form-adicionar-colaborador');
      if (formEl) {
        formEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
        const firstInput = formEl.querySelector('input') as HTMLInputElement | null;
        if (firstInput) firstInput.focus();
      }
    }, 150);
  };

  const handleIncluirMaisAgendamentos = () => {
    setViewMode('lote_manual');
    setSearchParams({ tab: 'lote_manual' });
    setEditingEmployee(null);
    setIsNovoCadastroOpen(false);
  };

  const handleCancelForm = () => {
    setEditingEmployee(null);
    setIsNovoCadastroOpen(false);
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 50);
  };

  const handleRemoveEmployee = (index: number) => {
    const updated = employees.filter((_, i) => i !== index);
    setEmployees(updated);
    if (updated.length === 0) {
      limparRascunhoAgendamento();
      resetAllData();
      setEditingEmployee(null);
      setIsNovoCadastroOpen(false);
    } else {
      saveStepData({ employeesData: updated });
      syncEmployeesStorage(updated);
    }
  };

  const handleImportarPlanilha = (
    colaboradoresImportados: ValidarPlanilhaResult['validRows'],
  ) => {
    const formatados: Employee[] = colaboradoresImportados.map((item) => ({
      id: (item.id as string) || (item.colaborador_id ? String(item.colaborador_id) : `batch_${Date.now()}_${Math.random()}`),
      nome: String(item.nome || ''),
      cpf: String(item.cpf || ''),
      nascimento: item.nascimento ? String(item.nascimento) : undefined,
      sexo: item.sexo ? String(item.sexo) : undefined,
      telefone: item.telefone ? String(item.telefone) : undefined,
      email: item.email ? String(item.email) : undefined,
      cargo: item.cargo ? String(item.cargo) : undefined,
      setor: item.setor ? String(item.setor) : undefined,
      exame: item.exame ? String(item.exame) : '1',
      is_candidato: Boolean(item.is_candidato),
      escolha_clinica_tipo: item.escolha_clinica_tipo === 'colaborador' ? 'colaborador' : 'rh',
      tipoContrato: item.tipo_contrato ? String(item.tipo_contrato) : undefined,
      dataAdmissao: item.data_admissao ? String(item.data_admissao) : undefined,
      matricula_esocial: item.matricula_esocial ? String(item.matricula_esocial) : undefined,
      nome_social: item.nome_social ? String(item.nome_social) : undefined,
      pcd: item.pcd !== undefined ? Boolean(item.pcd) : undefined,
      enviar_esocial: item.enviar_esocial !== undefined ? Boolean(item.enviar_esocial) : undefined,
      regime_contratacao: item.regime_contratacao ? String(item.regime_contratacao) : undefined,
      estado_civil: item.estado_civil ? String(item.estado_civil) : undefined,
      clinica: item.clinica_id ? String(item.clinica_id) : undefined,
      clinicaNome: item.clinica_nome ? String(item.clinica_nome) : undefined,
      dataAgendamento: item.data_agendamento ? String(item.data_agendamento) : undefined,
      demissao: item.data_demissao ? String(item.data_demissao) : undefined,
      is_existente: Boolean(item.colaborador_id),
      cargo_id: item.cargo_id,
      setor_id: item.setor_id,
    }));

    setEmployees(formatados);
    saveStepData({ employeesData: formatados });
    syncEmployeesStorage(formatados);
    goToNextStep();
  };

  const handleContinuarFila = async () => {
    if (employees.length === 0) {
      alert('Adicione pelo menos um colaborador antes de continuar.');
      return;
    }

    // Validar regras de negócio para cada colaborador antes de prosseguir
    for (const emp of employees) {
      if (!emp.exame || emp.exame === '') {
        emp.exame = admissionalId || '1';
      }
      if (emp.exame === demissionalId && (!emp.demissao || emp.demissao.trim() === '')) {
        alert(`O colaborador "${emp.nome}" possui exame Demissional selecionado. Por favor, preencha a Data de Demissão.`);
        return;
      }
      if (emp.escolha_clinica_tipo !== 'colaborador') {
        if (!emp.clinica || emp.clinica.trim() === '') {
          alert(`O colaborador "${emp.nome}" está configurado para "RH Escolhe Agora". Por favor, selecione uma clínica de atendimento.`);
          return;
        }
        if (!emp.dataAgendamento || emp.dataAgendamento.trim() === '') {
          alert(`O colaborador "${emp.nome}" está configurado para "RH Escolhe Agora". Por favor, selecione a data do agendamento.`);
          return;
        }
      } else {
        // Validação rigorosa de canais para "Link ao Colaborador" (Autoagendamento)
        const cleanPhone = emp.telefone ? String(emp.telefone).replace(/\D/g, '') : '';
        const cleanEmail = emp.email ? String(emp.email).trim() : '';
        const hasPhone = cleanPhone.length >= 10;
        const hasEmail = cleanEmail.length > 3 && cleanEmail.includes('@');

        if (!hasPhone && !hasEmail) {
          alert(`O colaborador "${emp.nome}" foi configurado para "Link ao Colaborador", mas não possui Telefone nem E-mail cadastrado. Por favor, adicione ao menos um canal de contato antes de continuar.`);
          setEditingContatoEmployee(emp);
          return;
        }
      }
    }

    saveStepData({ employeesData: employees });
    syncEmployeesStorage(employees);

    // Verificação de persistência atômica no storage
    const saved = localStorage.getItem('customer_employeesData');
    if (!saved) {
      console.error('❌ [Cadastro] Erro: Dados não foram salvos no localStorage');
      alert('Erro ao salvar dados do agendamento. Tente novamente.');
      return;
    }
    await new Promise((resolve) => setTimeout(resolve, 50));
    goToNextStep();
  };

  const isHistoricoActive = viewMode === 'historico';
  const isNovoActive = viewMode !== 'historico' && viewMode !== 'hub';

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-emerald-50/20 py-6 sm:py-8">
      <div className="container mx-auto px-4 max-w-6xl">
        {/* ========================================================= */}
        {/* BARRA SUPERIOR PERSISTENTE (NUNCA ESCONDIDA)             */}
        {/* ========================================================= */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 sm:p-5 mb-6 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <button
              onClick={() => {
                setViewMode('hub');
                setSearchParams({});
              }}
              className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-700 flex items-center justify-center shrink-0 hover:bg-emerald-100 transition-colors"
              title="Ir para o Hub Principal"
            >
              <Calendar className="w-5 h-5" />
            </button>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-base sm:text-lg font-bold text-gray-900 leading-tight">
                  Agendamento de Exames Ocupacionais
                </h1>
                <span className="hidden sm:inline-block text-[10px] uppercase font-bold tracking-wider text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-200">
                  Portal Corporativo
                </span>
              </div>
              <p className="text-xs text-gray-500 mt-0.5">
                Acompanhe exames realizados ou solicite novos agendamentos para sua empresa.
              </p>
            </div>
          </div>

          {/* CONTROLE SEGMENTADO DE ALTA VISIBILIDADE */}
          <div className="flex items-center bg-gray-100/90 p-1 rounded-xl border border-gray-200 self-start md:self-auto">
            <button
              onClick={() => handleSelectTab('historico')}
              className={`flex items-center gap-2 px-3.5 py-2 rounded-lg text-xs font-bold transition-all ${
                isHistoricoActive
                  ? 'bg-white text-emerald-800 shadow-sm ring-1 ring-black/5'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <ClipboardList className="w-4 h-4 text-emerald-600" />
              <span>Acompanhar Agendamentos</span>
              {historicoStats.total > 0 && (
                <span className="bg-emerald-100 text-emerald-800 text-[10px] px-2 py-0.5 rounded-full font-extrabold">
                  {historicoStats.total}
                </span>
              )}
            </button>

            <button
              onClick={() => handleSelectTab('novo')}
              className={`flex items-center gap-2 px-3.5 py-2 rounded-lg text-xs font-bold transition-all ${
                isNovoActive
                  ? 'bg-emerald-600 text-white shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <Plus className="w-4 h-4" />
              <span>Novo Agendamento</span>
            </button>
          </div>
        </div>

        {/* ========================================================= */}
        {/* VISTA 1: HUB DE ENTRADA (QUEBRA PRINCIPAL)               */}
        {/* ========================================================= */}
        {viewMode === 'hub' && (
          <div className="space-y-6 max-w-4xl mx-auto pt-2">
            {/* Banner de Rascunho em Andamento (se houver) */}
            {employees.length > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4 shadow-xs">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                  <div>
                    <h4 className="text-sm font-bold text-amber-900">
                      Rascunho de agendamento em andamento detectado
                    </h4>
                    <p className="text-xs text-amber-700 mt-0.5">
                      Você possui {employees.length} colaborador(es) aguardando finalização do agendamento.
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    onClick={handleDescartarRascunho}
                    className="px-3 py-1.5 text-xs font-semibold text-gray-600 hover:text-red-700 bg-white hover:bg-red-50 border border-gray-200 hover:border-red-200 rounded-lg transition-colors"
                  >
                    Descartar
                  </button>
                  <button
                    onClick={handleContinuarRascunho}
                    className="px-4 py-1.5 text-xs font-bold text-white bg-amber-600 hover:bg-amber-700 rounded-lg shadow-sm transition-colors"
                  >
                    Continuar Rascunho &rarr;
                  </button>
                </div>
              </div>
            )}

            {/* Cabeçalho de Boas-vindas do Hub */}
            <div className="text-center space-y-2 py-2">
              <span className="text-[11px] font-extrabold uppercase tracking-wider text-emerald-800 bg-emerald-100/70 px-3 py-1 rounded-full border border-emerald-200/80 inline-flex items-center gap-1.5">
                <Sparkles className="w-3.5 h-3.5 text-emerald-600" />
                Painel Corporativo de Saúde Ocupacional
              </span>
              <h2 className="text-2xl sm:text-3xl font-black text-gray-900 tracking-tight">
                Como podemos ajudar sua empresa hoje?
              </h2>
              <p className="text-gray-600 max-w-xl mx-auto text-xs sm:text-sm leading-relaxed">
                Selecione abaixo se deseja consultar as solicitações já emitidas ou iniciar um novo processo de agendamento de exames.
              </p>
            </div>

            {/* OS 2 CARDS EXECUTIVOS DA ENTRADA */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-2">
              {/* CARD 1: ACOMPANHAR AGENDAMENTOS JÁ REALIZADOS */}
              <div
                onClick={() => handleSelectTab('historico')}
                className="group relative bg-white rounded-2xl p-7 sm:p-8 shadow-sm hover:shadow-xl border-2 border-gray-200 hover:border-emerald-500 cursor-pointer transition-all duration-300 transform hover:-translate-y-1 flex flex-col justify-between"
              >
                <div>
                  <div className="flex items-center justify-between mb-5">
                    <div className="w-14 h-14 bg-emerald-50 text-emerald-700 rounded-2xl flex items-center justify-center group-hover:scale-110 group-hover:bg-emerald-600 group-hover:text-white transition-all duration-300">
                      <ClipboardList className="w-7 h-7" />
                    </div>
                    {historicoStats.total > 0 ? (
                      <div className="text-right">
                        <span className="bg-emerald-100 text-emerald-800 text-xs font-black px-3 py-1 rounded-full border border-emerald-200 inline-block">
                          {historicoStats.total} {historicoStats.total === 1 ? 'Agendamento Ativo' : 'Agendamentos Ativos'}
                        </span>
                        <span className="text-[10px] text-gray-400 block mt-0.5">Próximos 30 dias / Pendentes</span>
                      </div>
                    ) : (
                      <span className="bg-gray-100 text-gray-500 text-[11px] font-semibold px-2.5 py-1 rounded-full">
                        Sem pendências
                      </span>
                    )}
                  </div>

                  <h3 className="text-xl font-extrabold text-gray-900 group-hover:text-emerald-700 transition-colors">
                    Acompanhar Agendamentos
                  </h3>
                  <p className="text-xs sm:text-sm text-gray-500 mt-2 leading-relaxed">
                    Consulte exames já solicitados pela empresa ou pelos colaboradores via autoagendamento, acompanhe o status em tempo real nas credenciadas e emita guias de atendimento.
                  </p>

                  {/* Sub-estatísticas rápidas com segregação clara de ativos vs concluídos */}
                  {(historicoStats.total > 0 || historicoStats.concluidos > 0) && (
                    <div className="mt-4 pt-3 border-t border-gray-100 flex flex-wrap items-center gap-1.5 text-[11px]">
                      {historicoStats.autoagendamentos > 0 && (
                        <span className="bg-blue-50 text-blue-700 font-semibold px-2 py-0.5 rounded-md border border-blue-100">
                          {historicoStats.autoagendamentos} Autoagendamentos
                        </span>
                      )}
                      {historicoStats.rh > 0 && (
                        <span className="bg-purple-50 text-purple-700 font-semibold px-2 py-0.5 rounded-md border border-purple-100">
                          {historicoStats.rh} Via RH
                        </span>
                      )}
                      {historicoStats.concluidos > 0 && (
                        <>
                          <span className="text-gray-300 mx-0.5">|</span>
                          <span className="bg-teal-50 text-teal-700 font-semibold px-2 py-0.5 rounded-md border border-teal-200">
                            {historicoStats.concluidos} Concluídos no período
                          </span>
                        </>
                      )}
                    </div>
                  )}
                </div>

                <div className="mt-8 pt-4 border-t border-gray-100 flex items-center justify-between text-xs font-bold text-emerald-700">
                  <span>Ver lista completa e guias</span>
                  <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </div>
              </div>

              {/* CARD 2: NOVO AGENDAMENTO / NOVOS EXAMES */}
              <div
                onClick={() => handleSelectTab('novo')}
                className="group relative bg-gradient-to-br from-emerald-600 to-teal-700 text-white rounded-2xl p-7 sm:p-8 shadow-lg hover:shadow-2xl cursor-pointer transition-all duration-300 transform hover:-translate-y-1.5 flex flex-col justify-between"
              >
                <div>
                  <div className="flex items-center justify-between mb-5">
                    <div className="w-14 h-14 bg-white/15 text-white rounded-2xl flex items-center justify-center group-hover:scale-110 group-hover:bg-white group-hover:text-emerald-700 transition-all duration-300">
                      <Calendar className="w-7 h-7" />
                    </div>
                    <span className="bg-white/20 text-white text-[11px] font-bold px-2.5 py-1 rounded-full border border-white/20">
                      Novo Exame
                    </span>
                  </div>

                  <h3 className="text-xl font-extrabold text-white">
                    Novo Agendamento
                  </h3>
                  <p className="text-xs sm:text-sm text-emerald-100 mt-2 leading-relaxed">
                    Agende exames admissionais, periódicos ou demissionais para 1 colaborador ou em lote para múltiplos profissionais.
                  </p>
                </div>

                <div className="mt-8 pt-4 border-t border-white/20 flex items-center justify-between text-xs font-bold text-emerald-100">
                  <span>Iniciar novo agendamento</span>
                  <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ========================================================= */}
        {/* VISTA 2: ACOMPANHAR AGENDAMENTOS (HISTÓRICO)             */}
        {/* ========================================================= */}
        {viewMode === 'historico' && (
          <div className="space-y-4">
            <button
              onClick={() => {
                setViewMode('hub');
                setSearchParams({});
              }}
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-gray-600 hover:text-gray-900 bg-white px-3.5 py-2 rounded-xl border border-gray-200 transition-colors"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              <span>Voltar ao Início do Agendamento</span>
            </button>
            <HistoricoAgendamentos
              onNovoAgendamento={() => handleSelectTab('novo')}
              onStatsChange={setHistoricoStats}
            />
          </div>
        )}

        {/* ========================================================= */}
        {/* VISTA 3: ESCOLHA ENTRE INDIVIDUAL E MÚLTIPLOS             */}
        {/* ========================================================= */}
        {viewMode === 'modo_selecao' && (
          <div className="space-y-6 max-w-4xl mx-auto pt-2">
            <button
              onClick={() => {
                navigateToMode('hub', () => setSearchParams({}));
              }}
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-gray-600 hover:text-gray-900 bg-white px-3.5 py-2 rounded-xl border border-gray-200 transition-colors"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              <span>Voltar ao Início</span>
            </button>

            <div className="text-center space-y-2 mb-6">
              <span className="text-[11px] font-bold uppercase tracking-wider text-emerald-700 bg-emerald-50 px-2.5 py-1 rounded-md border border-emerald-100">
                Passo 1 de 2 • Definição do Formato
              </span>
              <h2 className="text-2xl sm:text-3xl font-extrabold text-gray-900">
                Como você deseja realizar o agendamento?
              </h2>
              <p className="text-xs sm:text-sm text-gray-500 max-w-lg mx-auto">
                Selecione o formato mais ágil para a demanda da sua equipe hoje.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* OPÇÃO 1: INDIVIDUAL */}
              <div
                onClick={() =>
                  navigateToMode('individual', () => {
                    setSearchParams({ tab: 'individual' });
                    if (employees.length === 1) {
                      setEditingEmployee(employees[0]);
                    }
                  })
                }
                className={`group bg-white rounded-2xl p-7 sm:p-8 border-2 border-gray-200 hover:border-emerald-500 cursor-pointer shadow-sm hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1.5 flex flex-col justify-between ${
                  cardAnimState === 'exiting' ? 'animate-card-exit' : 'animate-card-entrance'
                }`}
                style={{ animationDelay: '0ms' }}
              >
                <div>
                  <div className="w-12 h-12 bg-emerald-50 text-emerald-600 rounded-2xl flex items-center justify-center mb-5 group-hover:scale-110 group-hover:bg-emerald-600 group-hover:text-white transition-all">
                    <User className="w-6 h-6" />
                  </div>
                  <span className="text-[11px] font-bold uppercase tracking-wider text-emerald-700 bg-emerald-50 px-2.5 py-1 rounded-md">
                    Rápido & Direto
                  </span>
                  <h3 className="text-2xl sm:text-3xl font-black text-gray-900 mt-3 group-hover:text-emerald-700 transition-colors leading-tight">
                    Apenas 1
                    <span className="block text-sm sm:text-base font-semibold text-gray-500 group-hover:text-emerald-600/80 mt-1">
                      agendamento
                    </span>
                  </h3>
                  <p className="text-xs sm:text-sm text-gray-500 mt-3 leading-relaxed">
                    Formulário simples e focado para agendar um único colaborador existente ou candidato a exame admissional.
                  </p>
                </div>
                <div className="mt-8 pt-4 border-t border-gray-100 flex items-center justify-between text-xs font-bold text-emerald-700">
                  <span>Preencher dados do colaborador</span>
                  <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </div>
              </div>

              {/* OPÇÃO 2: MÚLTIPLOS (LOTE) */}
              <div
                onClick={() => navigateToMode('lote_escolha', () => setSearchParams({ tab: 'lote_escolha' }))}
                className={`group bg-white rounded-2xl p-7 sm:p-8 border-2 border-gray-200 hover:border-emerald-500 cursor-pointer shadow-sm hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1.5 flex flex-col justify-between ${
                  cardAnimState === 'exiting' ? 'animate-card-exit' : 'animate-card-entrance'
                }`}
                style={{ animationDelay: '80ms' }}
              >
                <div>
                  <div className="w-12 h-12 bg-blue-50 text-blue-600 rounded-2xl flex items-center justify-center mb-5 group-hover:scale-110 group-hover:bg-blue-600 group-hover:text-white transition-all">
                    <Users className="w-6 h-6" />
                  </div>
                  <span className="text-[11px] font-bold uppercase tracking-wider text-blue-700 bg-blue-50 px-2.5 py-1 rounded-md">
                    Produtividade para Equipes
                  </span>
                  <h3 className="text-2xl sm:text-3xl font-black text-gray-900 mt-3 group-hover:text-blue-700 transition-colors leading-tight">
                    2 ou mais
                    <span className="block text-sm sm:text-base font-semibold text-gray-500 group-hover:text-blue-600/80 mt-1">
                      agendamentos
                    </span>
                  </h3>
                  <p className="text-xs sm:text-sm text-gray-500 mt-3 leading-relaxed">
                    Agende múltiplos colaboradores de uma vez só: importe via planilha inteligente com validação ou cadastre em fila manual.
                  </p>
                </div>
                <div className="mt-8 pt-4 border-t border-gray-100 flex items-center justify-between text-xs font-bold text-blue-700">
                  <span>Escolher modo de lote</span>
                  <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ========================================================= */}
        {/* VISTA 4: ESCOLHA DO MODO DE LOTE (Planilha vs Manual)     */}
        {/* ========================================================= */}
        {viewMode === 'lote_escolha' && (
          <div className="space-y-6 max-w-4xl mx-auto pt-2">
            <button
              onClick={() => navigateToMode('modo_selecao', () => setSearchParams({ tab: 'novo' }))}
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-gray-600 hover:text-gray-900 bg-white px-3.5 py-2 rounded-xl border border-gray-200 transition-colors"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              <span>Voltar às Opções de Agendamento</span>
            </button>

            <div className="text-center space-y-2 mb-6">
              <span className="text-[11px] font-bold uppercase tracking-wider text-emerald-700 bg-emerald-50 px-2.5 py-1 rounded-md border border-emerald-100">
                Agendamento em Lote
              </span>
              <h2 className="text-2xl sm:text-3xl font-extrabold text-gray-900">
                Como prefere cadastrar o lote?
              </h2>
              <p className="text-xs sm:text-sm text-gray-500 max-w-lg mx-auto">
                Use a planilha modelo pré-configurada com os cargos da sua empresa ou monte uma lista na própria tela.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* LOTE VIA PLANILHA */}
              <div
                onClick={() => navigateToMode('lote_planilha', () => setSearchParams({ tab: 'lote_planilha' }))}
                className={`group bg-white rounded-2xl p-7 sm:p-8 border-2 border-gray-200 hover:border-emerald-500 cursor-pointer shadow-sm hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1.5 flex flex-col justify-between ${
                  cardAnimState === 'exiting' ? 'animate-card-exit' : 'animate-card-entrance'
                }`}
                style={{ animationDelay: '0ms' }}
              >
                <div>
                  <div className="w-12 h-12 bg-emerald-50 text-emerald-600 rounded-2xl flex items-center justify-center mb-5 group-hover:scale-110 group-hover:bg-emerald-600 group-hover:text-white transition-all">
                    <FileSpreadsheet className="w-6 h-6" />
                  </div>
                  <span className="text-[11px] font-bold uppercase tracking-wider text-emerald-700 bg-emerald-50 px-2.5 py-1 rounded-md">
                    Recomendado para grandes equipes
                  </span>
                  <h3 className="text-2xl font-black text-gray-900 mt-3 group-hover:text-emerald-700 transition-colors">
                    Importar Planilha
                  </h3>
                  <p className="text-xs sm:text-sm text-gray-500 mt-2 leading-relaxed">
                    Baixe o modelo personalizado já preenchido com menus suspensos (dropdowns) de cargos e setores ativos da sua empresa, preencha no Excel e faça o upload com validação instantânea.
                  </p>
                </div>
                <div className="mt-8 pt-4 border-t border-gray-100 flex items-center justify-between text-xs font-bold text-emerald-700">
                  <span>Ir para Upload de Planilha</span>
                  <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </div>
              </div>

              {/* LOTE MANUAL */}
              <div
                onClick={() => navigateToMode('lote_manual', () => setSearchParams({ tab: 'lote_manual' }))}
                className={`group bg-white rounded-2xl p-7 sm:p-8 border-2 border-gray-200 hover:border-emerald-500 cursor-pointer shadow-sm hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1.5 flex flex-col justify-between ${
                  cardAnimState === 'exiting' ? 'animate-card-exit' : 'animate-card-entrance'
                }`}
                style={{ animationDelay: '80ms' }}
              >
                <div>
                  <div className="w-12 h-12 bg-gray-50 text-gray-600 rounded-2xl flex items-center justify-center mb-5 group-hover:scale-110 group-hover:bg-gray-800 group-hover:text-white transition-all">
                    <Users className="w-6 h-6" />
                  </div>
                  <span className="text-[11px] font-bold uppercase tracking-wider text-gray-700 bg-gray-100 px-2.5 py-1 rounded-md">
                    Inserção Manual
                  </span>
                  <h3 className="text-2xl font-black text-gray-900 mt-3 group-hover:text-gray-900 transition-colors">
                    Cadastrar Manualmente
                  </h3>
                  <p className="text-xs sm:text-sm text-gray-500 mt-2 leading-relaxed">
                    Cadastre os colaboradores um a um pela tela, montando uma lista que será agendada conjuntamente na etapa seguinte.
                  </p>
                </div>
                <div className="mt-8 pt-4 border-t border-gray-100 flex items-center justify-between text-xs font-bold text-gray-700">
                  <span>Inserir manualmente</span>
                  <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ========================================================= */}
        {/* VISTA 5: UPLOAD DE LOTE EM PLANILHA                       */}
        {/* ========================================================= */}
        {viewMode === 'lote_planilha' && (
          <div className="space-y-4">
            <button
              onClick={() => navigateToMode('lote_escolha')}
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-gray-600 hover:text-gray-900 bg-white px-3.5 py-2 rounded-xl border border-gray-200 transition-colors"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              <span>Voltar à Escolha de Modo</span>
            </button>
            <UploadLotePlanilha
              onImportar={handleImportarPlanilha}
              onVoltar={() => navigateToMode('lote_escolha')}
            />
          </div>
        )}

        {/* ========================================================= */}
        {/* VISTA 6: CADASTRO MANUAL (INDIVIDUAL OU EM FILA)          */}
        {/* ========================================================= */}
        {(viewMode === 'individual' || viewMode === 'lote_manual') && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <button
                onClick={() =>
                  viewMode === 'individual'
                    ? navigateToMode('modo_selecao')
                    : navigateToMode('lote_escolha')
                }
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-gray-600 hover:text-gray-900 bg-white px-3.5 py-2 rounded-xl border border-gray-200 transition-colors"
              >
                <ArrowLeft className="w-3.5 h-3.5" />
                <span>Voltar às Opções</span>
              </button>
              <span className="text-xs text-gray-400 font-medium">
                Modo: {viewMode === 'individual' ? 'Agendamento Individual' : 'Lote Manual em Fila'}
              </span>
            </div>

            {/* Stepper */}
            <StepIndicator currentStep={currentStep} />

            {/* ========================================================= */}
            {/* PAINEL DE INCLUSÃO (MODO SELETIVO: NOVO / CANDIDATO / BASE) */}
            {/* ========================================================= */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200/80 p-5 sm:p-6 space-y-4">
              <div className="border-b border-gray-100 pb-4">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-3">
                  <div>
                    <h3 className="font-bold text-gray-900 text-sm sm:text-base flex items-center gap-2">
                      <Users className="w-4 h-4 text-emerald-600" />
                      <span>Incluir Colaboradores no Agendamento</span>
                    </h3>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Escolha como deseja adicionar: novo candidato do zero, candidato já em contratação ou colaborador ativo da base.
                    </p>
                  </div>
                </div>

                {/* Sub-modo Segment Control */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 p-1.5 bg-slate-100 rounded-xl">
                  <button
                    type="button"
                    onClick={() => {
                      setModoInclusao('novo_candidato');
                      setEditingEmployee(null);
                      setIsNovoCadastroOpen(true);
                      setTimeout(() => {
                        const formEl = document.getElementById('form-adicionar-colaborador');
                        if (formEl) {
                          formEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
                          const firstInput = formEl.querySelector('input') as HTMLInputElement | null;
                          if (firstInput) firstInput.focus();
                        }
                      }, 100);
                    }}
                    className={`flex items-center justify-center gap-2 py-2.5 px-3 rounded-lg text-xs font-bold transition-all ${
                      modoInclusao === 'novo_candidato'
                        ? 'bg-emerald-600 text-white shadow-xs'
                        : 'text-gray-700 hover:bg-white/60'
                    }`}
                  >
                    <UserPlus className="w-4 h-4" />
                    <span>+ Novo Candidato (Admissional)</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      setModoInclusao('em_contratacao');
                      setIsNovoCadastroOpen(false);
                    }}
                    className={`flex items-center justify-center gap-2 py-2.5 px-3 rounded-lg text-xs font-bold transition-all ${
                      modoInclusao === 'em_contratacao'
                        ? 'bg-amber-600 text-white shadow-xs'
                        : 'text-gray-700 hover:bg-white/60'
                    }`}
                  >
                    <Search className="w-4 h-4" />
                    <span>Candidato em Contratação</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      setModoInclusao('ativo_base');
                      setIsNovoCadastroOpen(false);
                    }}
                    className={`flex items-center justify-center gap-2 py-2.5 px-3 rounded-lg text-xs font-bold transition-all ${
                      modoInclusao === 'ativo_base'
                        ? 'bg-blue-600 text-white shadow-xs'
                        : 'text-gray-700 hover:bg-white/60'
                    }`}
                  >
                    <Users className="w-4 h-4" />
                    <span>Colaborador Ativo da Base</span>
                  </button>
                </div>
              </div>

              {/* Área de Ação Dinâmica Conforme Modo Selecionado */}

              {modoInclusao === 'em_contratacao' && (
                <div className="space-y-2 bg-amber-50/50 p-4 rounded-xl border border-amber-200/60">
                  <div className="flex items-center gap-2 text-xs text-amber-900 font-semibold">
                    <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
                    <span>Candidatos com admissional pendente ou cancelado (aproveitamento do cadastro sem duplicar CPF):</span>
                  </div>
                  <CollaboratorSearch
                    onAddCollaborator={handleIncluirColaboradorExistente}
                    selectedIds={employees.map((e) => e.id).concat(employees.map((e) => (e.cpf || '').replace(/\D/g, '')))}
                    placeholder="Digite nome ou CPF do candidato para buscar na base..."
                    label="Buscar Candidato em Contratação"
                    filtroStatus="em_contratacao"
                  />
                </div>
              )}

              {modoInclusao === 'ativo_base' && (
                <div className="space-y-2 bg-blue-50/40 p-4 rounded-xl border border-blue-200/60">
                  <div className="flex items-center gap-2 text-xs text-blue-900 font-semibold">
                    <span className="w-2 h-2 rounded-full bg-blue-500" />
                    <span>Colaboradores já admitidos (para exames Periódico, Demissional, Retorno ao Trabalho ou Mudança de Riscos):</span>
                  </div>
                  <CollaboratorSearch
                    onAddCollaborator={handleIncluirColaboradorExistente}
                    selectedIds={employees.map((e) => e.id).concat(employees.map((e) => (e.cpf || '').replace(/\D/g, '')))}
                    placeholder="Digite nome ou CPF para buscar na base de colaboradores ativos..."
                    label="Buscar Colaborador Ativo da Empresa"
                    filtroStatus="ativos"
                  />
                </div>
              )}
            </div>

            {/* ========================================================= */}
            {/* FORMULÁRIO DE NOVO CADASTRO / EDIÇÃO                      */}
            {/* ========================================================= */}
            {shouldShowForm && (
              <div
                id="form-adicionar-colaborador"
                className={`bg-white rounded-2xl shadow-sm border border-gray-200 p-6 sm:p-8 relative ${
                  loading ? 'opacity-80 pointer-events-none' : ''
                }`}
              >
                {loading && (
                  <div className="absolute inset-0 bg-white/70 backdrop-blur-[2px] flex items-center justify-center rounded-2xl z-20">
                    <div className="flex items-center gap-3 bg-white px-5 py-3 rounded-xl shadow-lg border border-gray-100">
                      <div className="w-5 h-5 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin" />
                      <span className="text-sm font-semibold text-gray-700">Registrando colaborador...</span>
                    </div>
                  </div>
                )}
                <div className="mb-6 pb-4 border-b border-gray-100 flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-bold text-gray-900">
                      {editingEmployee
                        ? `Editar Dados de ${editingEmployee.nome}`
                        : 'Cadastrar Novo Colaborador (Exame Admissional)'}
                    </h3>
                    <p className="text-xs text-gray-500 mt-1">
                      Preencha os dados cadastrais do profissional. O exame será registrado exclusivamente como Admissional.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={handleCancelForm}
                    className="text-xs font-bold text-gray-500 hover:text-gray-800 bg-gray-100 hover:bg-gray-200 px-3.5 py-1.5 rounded-xl transition-colors"
                  >
                    Cancelar
                  </button>
                </div>

                <EmployeeForm
                  key={editingEmployee ? `edit_${editingEmployee.id}` : `new_${modoInclusao}_${formKey}`}
                  onAddEmployee={handleAddEmployee}
                  editingEmployee={editingEmployee}
                  onCancelEdit={handleCancelForm}
                  defaultOpen={true}
                  hideAccordion={true}
                  hasEmployees={employees.length > 0}
                  defaultIsCandidato={modoInclusao === 'novo_candidato'}
                  defaultEscolhaClinicaTipo={modoInclusao === 'novo_candidato' ? 'colaborador' : 'rh'}
                />
              </div>
            )}

            {/* ========================================================= */}
            {/* FILA DE COLABORADORES NO AGENDAMENTO                      */}
            {/* ========================================================= */}
            {employees.length > 0 ? (
              <div id="fila-agendamento-section" className="bg-white rounded-2xl shadow-sm border border-gray-200/90 p-5 sm:p-6 space-y-4">
                {/* Banner de Ação Direta para Concluir o Agendamento */}
                <div className="bg-gradient-to-r from-emerald-50 via-teal-50 to-green-50 border border-emerald-200 rounded-xl p-3.5 flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-xs text-emerald-950 shadow-2xs">
                  <div className="flex items-center gap-2.5">
                    <span className="w-2.5 h-2.5 rounded-full bg-emerald-600 shrink-0 animate-pulse" />
                    <div>
                      <span className="font-bold">
                        {employees.length === 1
                          ? '1 profissional adicionado à fila.'
                          : `${employees.length} profissionais adicionados à fila.`}
                      </span>{' '}
                      <span className="text-emerald-800">
                        Para gerar o agendamento e emitir guias/links de atendimento, clique em avançar:
                      </span>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={handleContinuarFila}
                    className="inline-flex items-center justify-center gap-1.5 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs rounded-xl shadow-xs transition-colors shrink-0"
                  >
                    <span>Avançar para Agendamento</span>
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-3 border-b border-gray-100">
                  <div>
                    <h3 className="font-bold text-gray-900 text-sm sm:text-base flex items-center gap-2">
                      <span>Colaboradores na Fila do Agendamento</span>
                      <span className="bg-emerald-100 text-emerald-800 text-xs font-black px-2.5 py-0.5 rounded-full">
                        {employees.length}
                      </span>
                    </h3>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Defina o tipo de exame ocupacional e a clínica de atendimento para cada profissional.
                    </p>
                  </div>

                  <div className="flex items-center gap-2">
                    {viewMode === 'individual' && (
                      <button
                        type="button"
                        onClick={handleIncluirMaisAgendamentos}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-blue-700 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-xl transition-colors shadow-2xs"
                        title="Alternar para agendamento em lote e adicionar mais pessoas"
                      >
                        <Plus className="w-3.5 h-3.5" />
                        <span>Agendar Mais Colaboradores</span>
                      </button>
                    )}

                    <button
                      type="button"
                      onClick={() => {
                        if (confirm('Deseja descartar todos os colaboradores desta fila?')) {
                          limparRascunhoAgendamento();
                          setEmployees([]);
                          setEditingEmployee(null);
                          setIsNovoCadastroOpen(false);
                          resetAllData();
                        }
                      }}
                      className="px-3 py-1.5 text-xs font-semibold text-red-600 hover:text-red-700 bg-white hover:bg-red-50 border border-gray-200 hover:border-red-200 rounded-xl transition-colors"
                    >
                      Descartar Fila
                    </button>

                    <button
                      type="button"
                      onClick={handleContinuarFila}
                      className="flex items-center gap-1.5 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-xl shadow-sm transition-colors"
                    >
                      <span>Avançar para Agendamento ({employees.length})</span>
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                <div className="space-y-3">
                  {employees.map((emp, index) => {
                    const isExistente =
                      emp.is_existente !== undefined
                        ? emp.is_existente === true
                        : (!emp.id.startsWith('local_') && !emp.id.startsWith('batch_'));

                    return (
                      <div
                        key={emp.id}
                        className="bg-slate-50/70 hover:bg-slate-50 border border-gray-200 rounded-2xl p-4 sm:p-5 transition-all shadow-2xs space-y-4"
                      >
                        {/* Identificação do Colaborador */}
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                          <div className="flex items-start sm:items-center gap-3">
                            <div className="w-8 h-8 rounded-xl bg-white border border-gray-200 text-gray-700 flex items-center justify-center font-bold text-xs shrink-0 shadow-2xs">
                              {index + 1}
                            </div>
                            <div>
                              <div className="flex flex-wrap items-center gap-2">
                                <h4 className="font-extrabold text-sm text-gray-900">{emp.nome}</h4>
                                {isExistente ? (
                                  <span className="bg-blue-50 text-blue-800 text-[10px] font-bold px-2 py-0.5 rounded-full border border-blue-200 flex items-center gap-1">
                                    <UserCheck className="w-3 h-3 text-blue-600" /> Colaborador da Base
                                  </span>
                                ) : emp.is_candidato ? (
                                  <span className="bg-amber-100 text-amber-800 text-[10px] font-bold px-2 py-0.5 rounded-full border border-amber-200">
                                    Candidato (Retenção eSocial)
                                  </span>
                                ) : (
                                  <span className="bg-emerald-100 text-emerald-800 text-[10px] font-bold px-2 py-0.5 rounded-full border border-emerald-200">
                                    Novo Cadastro • Admissional
                                  </span>
                                )}
                              </div>
                              <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs text-gray-500 mt-1 font-mono">
                                <span>CPF: {formatCPF(emp.cpf)}</span>
                                {emp.email ? (
                                  <span
                                    className="font-sans text-gray-700 bg-white border border-gray-200 px-2 py-0.5 rounded text-[11px] flex items-center gap-1 shadow-2xs"
                                    title={`E-mail: ${emp.email}`}
                                  >
                                    <Mail className="w-3 h-3 text-emerald-600 shrink-0" />
                                    <span>{maskEmail(emp.email)}</span>
                                  </span>
                                ) : (
                                  <span className="font-sans text-gray-400 bg-gray-50 border border-gray-200 px-2 py-0.5 rounded text-[11px] flex items-center gap-1">
                                    <Mail className="w-3 h-3 text-gray-300 shrink-0" />
                                    <span>Sem e-mail</span>
                                  </span>
                                )}
                                {emp.telefone ? (
                                  <span
                                    className="font-sans text-gray-700 bg-white border border-gray-200 px-2 py-0.5 rounded text-[11px] flex items-center gap-1 shadow-2xs"
                                    title={`Telefone: ${emp.telefone}`}
                                  >
                                    <Phone className="w-3 h-3 text-emerald-600 shrink-0" />
                                    <span>{maskTelefone(emp.telefone)}</span>
                                  </span>
                                ) : (
                                  <span className="font-sans text-gray-400 bg-gray-50 border border-gray-200 px-2 py-0.5 rounded text-[11px] flex items-center gap-1">
                                    <Phone className="w-3 h-3 text-gray-300 shrink-0" />
                                    <span>Sem telefone</span>
                                  </span>
                                )}
                                {emp.cargo && (
                                  <span className="font-sans text-gray-700 bg-white border border-gray-200 px-2 py-0.5 rounded text-[11px]">
                                    {emp.cargo}
                                  </span>
                                )}
                                {emp.setor && (
                                  <span className="font-sans text-gray-500 text-[11px]">
                                    Setor: {emp.setor}
                                  </span>
                                )}
                                {emp.dataAdmissao && (
                                  <span className="font-sans text-gray-500 text-[11px]">
                                    Admissão: {emp.dataAdmissao}
                                  </span>
                                )}
                                {emp.escolha_clinica_tipo === 'colaborador' ? (
                                  <div className="inline-flex items-center gap-1.5 flex-wrap">
                                    <span className="font-sans text-blue-800 bg-blue-50 border border-blue-200 px-2 py-0.5 rounded text-[11px] font-medium flex items-center gap-1">
                                      <Clock className="w-3 h-3 text-blue-600" />
                                      Autoagendamento via Link
                                    </span>
                                    {emp.telefone && emp.email ? (
                                      <span className="font-sans text-emerald-800 bg-emerald-50 border border-emerald-300 px-2 py-0.5 rounded text-[11px] font-bold flex items-center gap-1" title="O colaborador receberá o link por WhatsApp e por E-mail">
                                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                                        WhatsApp + E-mail
                                      </span>
                                    ) : emp.telefone && !emp.email ? (
                                      <span className="font-sans text-sky-800 bg-sky-50 border border-sky-300 px-2 py-0.5 rounded text-[11px] font-bold flex items-center gap-1" title="O colaborador receberá o link apenas por WhatsApp">
                                        <span className="w-1.5 h-1.5 rounded-full bg-sky-500" />
                                        Apenas WhatsApp
                                      </span>
                                    ) : !emp.telefone && emp.email ? (
                                      <button
                                        type="button"
                                        onClick={() => setEditingContatoEmployee(emp)}
                                        className="font-sans text-amber-800 bg-amber-50 hover:bg-amber-100 border border-amber-300 px-2 py-0.5 rounded text-[11px] font-bold flex items-center gap-1 transition-colors cursor-pointer"
                                        title="Clique para cadastrar o WhatsApp do colaborador"
                                      >
                                        <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                                        <span>Apenas E-mail (+ WhatsApp)</span>
                                      </button>
                                    ) : (
                                      <button
                                        type="button"
                                        onClick={() => setEditingContatoEmployee(emp)}
                                        className="font-sans text-red-800 bg-red-50 hover:bg-red-100 border border-red-300 px-2 py-0.5 rounded text-[11px] font-bold flex items-center gap-1 animate-pulse transition-colors cursor-pointer"
                                        title="Atenção: Cadastre WhatsApp ou E-mail para enviar o link"
                                      >
                                        <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
                                        <span>Sem Contato (+ Cadastrar)</span>
                                      </button>
                                    )}
                                  </div>
                                ) : (
                                  <>
                                    {emp.clinicaNome && (
                                      <span className="font-sans text-emerald-800 bg-emerald-50 border border-emerald-300 px-2 py-0.5 rounded text-[11px] font-bold flex items-center gap-1">
                                        <Building className="w-3 h-3 text-emerald-600" />
                                        Clínica: {emp.clinicaNome}
                                      </span>
                                    )}
                                    {emp.dataAgendamento && (
                                      <span className="font-sans text-emerald-800 bg-emerald-50 border border-emerald-300 px-2 py-0.5 rounded text-[11px] font-bold flex items-center gap-1">
                                        <Calendar className="w-3 h-3 text-emerald-600" />
                                        Data: {emp.dataAgendamento}
                                      </span>
                                    )}
                                  </>
                                )}
                              </div>
                            </div>
                          </div>

                          <div className="flex items-center gap-1.5 self-end sm:self-auto">
                            <button
                              type="button"
                              onClick={() => setEditingContatoEmployee(emp)}
                              className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold text-emerald-700 hover:text-emerald-800 bg-white hover:bg-emerald-50 border border-gray-200 hover:border-emerald-300 rounded-lg transition-colors cursor-pointer shadow-2xs"
                              title="Editar telefone celular / WhatsApp e e-mail"
                            >
                              <Phone className="w-3.5 h-3.5 text-emerald-600" />
                              <span>Contato</span>
                            </button>
                            {!isExistente && (
                              <button
                                type="button"
                                onClick={() => handleEditEmployee(index)}
                                className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold text-gray-600 hover:text-emerald-700 bg-white hover:bg-emerald-50 border border-gray-200 rounded-lg transition-colors"
                                title="Editar dados cadastrais"
                              >
                                <Edit3 className="w-3.5 h-3.5" />
                                <span>Editar</span>
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => handleRemoveEmployee(index)}
                              className="p-1.5 text-gray-400 hover:text-red-700 hover:bg-red-50 rounded-lg border border-transparent hover:border-red-200 transition-colors"
                              title="Remover da fila"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>

                        {/* Configuração Específica do Agendamento */}
                        <div className="bg-white p-3.5 sm:p-4 rounded-xl border border-gray-200/80 grid grid-cols-1 md:grid-cols-2 gap-4">
                          {/* LADO ESQUERDO: TIPO DE EXAME */}
                          <div>
                            <label className="block text-[11px] font-bold text-gray-700 uppercase tracking-wider mb-1.5">
                              Tipo de Exame Ocupacional *
                            </label>
                            {isExistente ? (
                              <div className="space-y-2">
                                <select
                                  value={emp.exame || periodicoId}
                                  onChange={(e) => handleUpdateEmployeeExam(emp.id, e.target.value)}
                                  className="w-full px-3 py-2 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 bg-white font-medium text-gray-900 shadow-2xs"
                                >
                                  {tiposExame.map((t) => (
                                    <option key={t.id} value={t.id.toString()}>
                                      {t.nome}
                                    </option>
                                  ))}
                                </select>

                                {/* Data de Demissão se for Demissional */}
                                {emp.exame === demissionalId && (
                                  <div className="pt-1.5 animate-in fade-in duration-200">
                                    <label className="block text-[10px] font-bold text-red-700 uppercase tracking-wider mb-1">
                                      Data de Demissão *
                                    </label>
                                    <input
                                      type="date"
                                      value={emp.demissao || ''}
                                      onChange={(e) => handleUpdateEmployeeDemissao(emp.id, e.target.value)}
                                      className="w-full px-3 py-1.5 text-xs border border-red-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 bg-red-50/30 text-gray-900"
                                      required
                                    />
                                  </div>
                                )}
                              </div>
                            ) : (
                              <div className="flex items-center gap-2 px-3 py-2 bg-emerald-50/70 border border-emerald-200 rounded-lg">
                                <span className="w-2 h-2 rounded-full bg-emerald-600 shrink-0" />
                                <span className="text-xs font-bold text-emerald-900">
                                  Exame Admissional (Novo Vínculo)
                                </span>
                              </div>
                            )}
                          </div>

                          {/* LADO DIREITO: QUEM DEFINE A CLÍNICA */}
                          <div>
                            <label className="block text-[11px] font-bold text-gray-700 uppercase tracking-wider mb-1.5">
                              Definição da Clínica de Atendimento *
                            </label>
                            <div className="grid grid-cols-2 gap-2">
                              <button
                                type="button"
                                onClick={() => handleUpdateEmployeeClinicaTipo(emp.id, 'rh')}
                                className={`px-3 py-2 text-xs font-semibold rounded-lg border transition-all flex items-center justify-center gap-1.5 ${
                                  emp.escolha_clinica_tipo !== 'colaborador'
                                    ? 'bg-emerald-600 text-white border-emerald-600 shadow-2xs'
                                    : 'bg-white text-gray-600 hover:text-gray-900 border-gray-200 hover:border-gray-300'
                                }`}
                              >
                                <Building className="w-3.5 h-3.5" />
                                <span>RH Escolhe Agora</span>
                              </button>

                              <button
                                type="button"
                                onClick={() => handleUpdateEmployeeClinicaTipo(emp.id, 'colaborador')}
                                className={`px-3 py-2 text-xs font-semibold rounded-lg border transition-all flex items-center justify-center gap-1.5 ${
                                  emp.escolha_clinica_tipo === 'colaborador'
                                    ? 'bg-emerald-600 text-white border-emerald-600 shadow-2xs'
                                    : 'bg-white text-gray-600 hover:text-gray-900 border-gray-200 hover:border-gray-300'
                                }`}
                              >
                                <Clock className="w-3.5 h-3.5" />
                                <span>Link ao Colaborador</span>
                              </button>
                            </div>

                            {/* Banner informativo de canais ativos */}
                            {emp.escolha_clinica_tipo === 'colaborador' && (
                              <div className="mt-2.5 p-2.5 rounded-xl border text-xs flex items-center justify-between gap-2 transition-all bg-slate-50 border-slate-200">
                                <div className="flex items-center gap-2">
                                  {emp.telefone && emp.email ? (
                                    <>
                                      <span className="w-2 h-2 rounded-full bg-emerald-500 shrink-0" />
                                      <span className="text-gray-700">Link será enviado por <strong>WhatsApp</strong> e <strong>E-mail</strong>.</span>
                                    </>
                                  ) : emp.telefone && !emp.email ? (
                                    <>
                                      <span className="w-2 h-2 rounded-full bg-sky-500 shrink-0" />
                                      <span className="text-gray-700">Link será enviado apenas por <strong>WhatsApp</strong>.</span>
                                    </>
                                  ) : !emp.telefone && emp.email ? (
                                    <>
                                      <span className="w-2 h-2 rounded-full bg-amber-500 shrink-0" />
                                      <span className="text-amber-800">Sem WhatsApp. O link irá <strong>apenas por E-mail</strong>.</span>
                                    </>
                                  ) : (
                                    <>
                                      <span className="w-2 h-2 rounded-full bg-red-500 shrink-0 animate-pulse" />
                                      <span className="text-red-700 font-semibold">Sem canais cadastrados!</span>
                                    </>
                                  )}
                                </div>
                                <button
                                  type="button"
                                  onClick={() => setEditingContatoEmployee(emp)}
                                  className="text-[11px] font-bold text-emerald-700 hover:text-emerald-800 underline shrink-0 cursor-pointer"
                                >
                                  {!emp.telefone ? '+ Cadastrar WhatsApp' : 'Editar Contato'}
                                </button>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Seção de Exames do Agendamento (Protocolo PCMSO + Exames Pontuais) */}
                        <div className="bg-white p-3.5 sm:p-4 rounded-xl border border-gray-200/80 shadow-2xs">
                          <ExamesPontuaisSearchInput
                            selectedExamIds={emp.examesSelecionados || []}
                            protocoloExamIds={emp.protocoloExames || []}
                            pontuaisExamIds={emp.examesPontuais || []}
                            catalogExames={catalogExames}
                            onAddExam={(examId) => handleAddPontualExam(emp.id, examId)}
                            onRemoveExam={(examId) => handleRemovePontualExam(emp.id, examId)}
                          />
                        </div>

                        {/* Painel de Configuração de Clínica e Data (RH Escolhe Agora) */}
                        {emp.escolha_clinica_tipo !== 'colaborador' && (
                          <ColaboradorClinicaPanel
                            key={`clinica_panel_${emp.id}`}
                            employee={emp}
                            onSelectClinica={(clinica) =>
                              handleUpdateEmployeeClinica(emp.id, clinica.id.toString(), clinica.nome)
                            }
                            onSelectDataAgendamento={(data) =>
                              handleUpdateEmployeeDataAgendamento(emp.id, data)
                            }
                          />
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Avisozinho Proativo: Colaborador(es) sem celular cadastrado para envio do link */}
                {employees.some(e => e.escolha_clinica_tipo === 'colaborador' && (!e.telefone || String(e.telefone).replace(/\D/g, '').length < 10) && (e.email && e.email.includes('@'))) && (
                  <div className="p-3.5 bg-amber-50/90 border border-amber-300 rounded-2xl text-xs text-amber-950 flex flex-col md:flex-row md:items-center justify-between gap-3 shadow-2xs">
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-xl bg-amber-100 border border-amber-300 flex items-center justify-center shrink-0">
                        <AlertTriangle className="w-4 h-4 text-amber-700" />
                      </div>
                      <div>
                        <p className="font-bold text-amber-950">Aviso sobre o envio do Link de Agendamento</p>
                        <p className="text-amber-800 text-[11px] mt-0.5">
                          Colaborador(es) sem celular cadastrado: o link será enviado <strong>apenas por E-mail</strong>. Se preferir enviar via WhatsApp, cadastre o número no atalho ao lado:
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap shrink-0">
                      {employees
                        .filter(e => e.escolha_clinica_tipo === 'colaborador' && (!e.telefone || String(e.telefone).replace(/\D/g, '').length < 10))
                        .map(emp => (
                          <button
                            key={`btn_contato_aviso_${emp.id}`}
                            type="button"
                            onClick={() => setEditingContatoEmployee(emp)}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-amber-950 bg-amber-200/80 hover:bg-amber-300 border border-amber-400 rounded-xl transition-colors cursor-pointer shadow-2xs"
                            title={`Cadastrar WhatsApp para ${emp.nome}`}
                          >
                            <Phone className="w-3.5 h-3.5 text-amber-800" />
                            <span>+ Preencher WhatsApp ({emp.nome.split(' ')[0]})</span>
                          </button>
                        ))}
                    </div>
                  </div>
                )}

                {/* Rodapé da Fila */}
                <div className="pt-4 flex flex-col sm:flex-row items-center justify-between gap-3 border-t border-gray-100">
                  <div className="text-xs text-gray-500 font-medium">
                    Total: <strong className="text-gray-900">{employees.length}</strong> {employees.length === 1 ? 'colaborador' : 'colaboradores'} pronto(s) para agendamento
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        setEditingEmployee(null);
                        setIsNovoCadastroOpen(true);
                        setTimeout(() => {
                          const formEl = document.getElementById('form-adicionar-colaborador');
                          if (formEl) formEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        }, 100);
                      }}
                      className="inline-flex items-center gap-1.5 px-3.5 py-2 text-xs font-bold text-emerald-700 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 rounded-xl transition-colors shadow-2xs"
                    >
                      <UserPlus className="w-4 h-4" />
                      <span>+ Cadastrar Novo Colaborador</span>
                    </button>

                    <button
                      type="button"
                      onClick={handleContinuarFila}
                      className="flex items-center gap-1.5 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-xl shadow-sm transition-colors"
                    >
                      <span>Avançar para Agendamento ({employees.length})</span>
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              !shouldShowForm && (
                <div className="bg-white border border-dashed border-gray-300 rounded-2xl p-8 sm:p-12 text-center max-w-2xl mx-auto space-y-4">
                  <div className="w-14 h-14 bg-emerald-50 text-emerald-600 rounded-2xl flex items-center justify-center mx-auto shadow-2xs">
                    <Users className="w-7 h-7" />
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-gray-900">
                      Nenhum colaborador adicionado ao agendamento ainda
                    </h3>
                    <p className="text-xs text-gray-500 max-w-md mx-auto mt-1 leading-relaxed">
                      Para agendar <strong>exames periódicos, demissionais, retorno ou mudança de função</strong>, pesquise no campo de busca acima e clique em <strong>+ Incluir</strong>. Para novos funcionários ou candidatos, utilize o botão abaixo.
                    </p>
                  </div>
                  <div className="pt-2">
                    <button
                      type="button"
                      onClick={() => {
                        setEditingEmployee(null);
                        setIsNovoCadastroOpen(true);
                      }}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-bold transition-colors shadow-sm"
                    >
                      <UserPlus className="w-4 h-4" />
                      <span>Cadastrar Novo Colaborador (Admissional)</span>
                    </button>
                  </div>
                </div>
              )
            )}
          </div>
        )}

        {/* Modal de Edição Rápida de Contato */}
        <EditarContatoModal
          isOpen={!!editingContatoEmployee}
          onClose={() => setEditingContatoEmployee(null)}
          colaborador={editingContatoEmployee}
          onSuccess={(contato) => {
            if (editingContatoEmployee) {
              handleUpdateEmployeeContato(editingContatoEmployee.id, contato);
            }
          }}
        />
      </div>
    </div>
  );
}
